import { X } from 'lucide-react'
import { type ReactNode, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Foot, type FootAction } from './Foot'
import { useDismissable } from './useDismissable'
import './foot.css'
import './modal.css'

/**
 * The one modal. A portal-mounted overlay + panel with a titled header, a
 * scrollable body and an optional footer. `size` widens the panel for content
 * that needs room (a long description, a two-column form). Screens compose it;
 * they never hand-roll an overlay.
 *
 * Its foot is the sheet's two buttons: `primary` answers the decision and
 * `secondary` declines it, Cancel where the caller names nothing. A dialog's
 * answer trails, so the pair sits together at the trailing edge. `footer`
 * remains for a foot that is not those two buttons.
 */
export function Modal({
  title,
  ariaLabel,
  onClose,
  size = 'default',
  footer,
  primary,
  secondary,
  children,
}: {
  title: ReactNode
  ariaLabel?: string
  onClose: () => void
  size?: 'default' | 'large' | 'wide'
  footer?: ReactNode
  /** The answer, named for what it does. Disabled until it can act. */
  primary?: FootAction
  /** The way out without answering; Cancel, closing the modal, by default. */
  secondary?: FootAction
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useDismissable(panel, onClose)
  const lead =
    secondary ?? (primary ? { label: 'Cancel', onSelect: onClose } : undefined)

  return createPortal(
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="presentation"
    >
      <div
        className="modal-panel"
        data-size={size}
        role="dialog"
        aria-modal="true"
        /* A dialog is named by its title unless a caller names it outright,
           which a title that is not plain words needs. */
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : titleId}
        ref={panel}
        tabIndex={-1}
      >
        <header className="modal-panel__head">
          <div className="modal-panel__title" id={titleId}>
            {title}
          </div>
          <button
            type="button"
            className="modal-panel__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} aria-hidden />
          </button>
        </header>
        <div className="modal-panel__body">{children}</div>
        {primary ? (
          <Foot
            primary={primary}
            secondary={lead}
            trail
            className="modal-panel__foot"
          />
        ) : footer ? (
          <footer className="uifoot modal-panel__foot">{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
